import { Input, Modal, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TableRowSelection } from "antd/es/table/interface";
import DocumentGrantedUsers from "./document-granted-users";
import { useDocumentContent } from "../../hooks";
import { DownCircleOutlined } from "@ant-design/icons";
import DecentralizeTitleTable, { DocEditor } from "./document-decentralize-title-table";
import dayjs from "dayjs";
import { v4 as uuid } from "uuid";
import { Key, useRef, useState } from "react";
import { getEnv } from "../../../../AppConfigs";
import api from "../../../../services/axiosBase";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "react-bootstrap";
import moment from "moment";
import { BATCHING_URL, DateFormat } from "../../../../constants";
import { MailService } from "../../../../services/mail/mail.service";
import { CreateNotification } from "../../../../components/notifier";
import difference from "lodash/difference";
import chunk from "lodash/chunk";
import { html } from "../../utils/html";
import { mailComposeTemplate, SendMailProps } from "../../utils/mailTemplate";

const SPSiteId = getEnv("REACT_APP_SP_SITE_ID");
const SPListId = getEnv("REACT_APP_DOCUMENT_CONTENTS_LIST_ID");

interface Props {
  document?: IDocument;
}

export enum Role {
  create = "create",
  read = "read",
  update = "update",
  delete = "delete",
}
export interface UserPermission {
  id?: string;
  roles: Role[];
  user: DocEditor;
  createdDateTime: string;
  updatedDateTime: string;
}

export interface IDocumentContent {
  Id?: string;
  Content: string;
  CustomId: string;
  CustomParentId?: string;
  Permissions?: any;
  Title: string;
  UserPermissions?: UserPermission[];
  children?: IDocumentContent[];
  IsContent: boolean;
  CreatedUser: { email: string; id: string };
  docEditors: any;
}

export interface IDocument {
  docEditors: string;
  effectiveFrom: string;
  effectiveTo: string;
  Title: string;
  docCode: string;
  userEmail: string;
  userDisplayName: string;
  listItemId: number;
  fileId: string;
}

type UserLogType = {
  key: string | Key;
  content: string;
  isContent: boolean;
  emails: string[];
};

interface EmailContentMap {
  [email: string]: string[];
}

type DataType = IDocumentContent & { children?: IDocumentContent[] };

// const mapDocumentToDataSource = (documents: DataType[]) => {
//   const documentMap: Record<string, IDocumentContent & { children: IDocumentContent[] }> = {};
//   const nestedDocuments: IDocumentContent[] = [];

//   documents.forEach((doc) => {
//     documentMap[doc.CustomId] = { ...doc, children: [] };
//   });

//   documents.forEach((doc) => {
//     if (doc.CustomParentId) {
//       const parent = documentMap[doc.CustomParentId];
//       if (parent) {
//         parent.children.push(documentMap[doc.CustomId]);
//       }
//     } else {
//       nestedDocuments.push(documentMap[doc.CustomId]);
//     }
//   });

//   return nestedDocuments;
// };

export const arrayNest = <T,>(data: T[], options: { idFieldName: keyof T; parentIdFieldName: keyof T }) => {
  const hashedMap = new Map<T[keyof T], T & { children: T[] }>();
  const nested: T[] = [];

  data.forEach((item) => hashedMap.set(item[options.idFieldName], { ...item, children: [] }));

  data.forEach((item) => {
    const parent = hashedMap.get(item[options.parentIdFieldName]);
    const current = hashedMap.get(item[options.idFieldName]) as T;
    if (item[options.parentIdFieldName] && parent) {
      parent.children.push(current);
    } else {
      nested.push(current);
    }
  });
  return nested;
};

const generateUserPermissions = ({ docEditors, roles }: { docEditors: DocEditor[]; roles: Role[] }) => {
  return docEditors.map<UserPermission>((user) => ({
    id: uuid(),
    roles,
    user,
    createdDateTime: dayjs().toISOString(),
    updatedDateTime: dayjs().toISOString(),
  }));
};

const generateRequestBatching = (
  data: {
    rowKey: Key;
    userPermissions: UserPermission[];
  }[],
) => {
  return data.map((item, idx) => ({
    id: (idx + 1).toString(),
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    url: `/sites/${SPSiteId}/lists/${SPListId}/items/${item.rowKey}/fields`,
    body: { UserPermissions: JSON.stringify(item.userPermissions) },
  }));
};

const updateDocumentUserPermission = async (body: { requests: any[] }) => {
  const chunked = chunk(body.requests, 15);
  return Promise.all(chunked.map(async (requests) => await api.post(BATCHING_URL, { requests })));
};

const DocumentViewDecentralize = (props: Props) => {
  const { document } = props;
  const queryClient = useQueryClient();
  const { isLoading: isDocumentContentLoading, data: documents = [] } = useDocumentContent();
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const detailUrl = getEnv("REACT_APP_DOCUMENT_DETAILS_URL");
  const selectedUserRef = useRef<UserLogType[]>([]);
  const loading = isDocumentContentLoading || isLoading;

  const docEditors = !document?.docEditors ? [] : JSON.parse(document?.docEditors ?? "");
  const disabledSelectUsers = selectedRowKeys.length === 0;

  const toggleLoading = (loading: boolean) => {
    setIsLoading(loading);
  };

  const handleUpdateDocument = async ({ requests, reset }: { requests: any[]; reset: () => void }) => {
    try {
      toggleLoading(true);
      await updateDocumentUserPermission({ requests });
      message.success("Cấp quyền thành công");
      reset();
      queryClient.invalidateQueries({ queryKey: ["document-content"] });
    } catch (error) {
      message.error("Cấp quyền thất bại");
    } finally {
      toggleLoading(false);
    }
  };

  const onDecentralizeRead = async (_: string[], docEditors: DocEditor[], { reset }: { reset: () => void }) => {
    // Role.Read
    const permissions = generateUserPermissions({ docEditors, roles: [Role.read] });
    const permissionEmails = permissions.map((e) => e.user.email);
    const logArr: UserLogType[] = [];

    const result = selectedRowKeys.map((rowKey) => {
      const map = new Map();
      const doc = documents.find((doc: any) => doc.Id === rowKey)!;

      const logAction = { key: rowKey, content: doc.Content, isContent: doc.IsContent, emails: [...permissionEmails] };
      logArr.push(logAction);

      const currentPermissions: UserPermission[] = doc?.UserPermissions ?? [];

      currentPermissions.forEach((i) => map.set(i.user.Id, i));

      permissions.forEach((permission) => {
        const existing = map.get(permission.user.Id);
        if (existing) {
          const newItem = { ...existing, roles: [...new Set([...existing.roles, Role.read])] };
          map.set(permission.user.Id, newItem);
        } else {
          map.set(permission.user.Id, permission);
        }
      });
      return { rowKey, userPermissions: Array.from(map, ([_, per]) => per) };
    });

    selectedUserRef.current = [...selectedUserRef.current, ...logArr];

    const requests = generateRequestBatching(result);

    handleUpdateDocument({ requests: requests, reset });
  };

  const onDecentralizeEdit = async (_: string[], docEditors: DocEditor[], { reset }: { reset: () => void }) => {
    const permissions = generateUserPermissions({ docEditors, roles: [Role.update] });
    const permissionEmails = permissions.map((e) => e.user.email);
    const logArr: UserLogType[] = [];

    const result = selectedRowKeys.map((rowKey) => {
      const map = new Map();
      const doc = documents.find((doc: any) => doc.Id === rowKey)!;

      const logAction = { key: rowKey, content: doc.Content, isContent: doc.IsContent, emails: [...permissionEmails] };
      logArr.push(logAction);

      const currentPermissions: UserPermission[] = doc?.UserPermissions ?? [];

      currentPermissions.forEach((i) => map.set(i.user.Id, i));

      permissions.forEach((permission) => {
        const existing = map.get(permission.user.Id);
        if (existing) {
          const newItem = { ...existing, roles: [...new Set([...existing.roles, Role.update])] };
          map.set(permission.user.Id, newItem);
        } else {
          map.set(permission.user.Id, permission);
        }
      });

      return { rowKey, userPermissions: Array.from(map, ([_, per]) => per) };
    });

    selectedUserRef.current = [...selectedUserRef.current, ...logArr];
    const requests = generateRequestBatching(result);

    handleUpdateDocument({ requests: requests, reset });
  };

  const onRevoke = async (
    { id, roles, user }: { id: string; roles: Role[]; user: DocEditor },
    document: IDocumentContent,
  ) => {
    const userPermissions = document?.UserPermissions ?? [];
    const docId = document.Id;
    const revokeIndex = selectedUserRef?.current.findIndex((e) => e.key === docId);
    if (revokeIndex > -1) {
      const newEmails = selectedUserRef?.current[revokeIndex].emails.filter((e) => e !== user.email);
      selectedUserRef.current[revokeIndex].emails = newEmails;
    }

    const result = userPermissions.map((item) => {
      if (item.id !== id) return item;
      const newRoles = difference(item.roles, roles);

      return { ...item, roles: newRoles };
    });

    const filteredUserPermissions = result.filter((item) => item?.roles?.length !== 0);

    const requests = generateRequestBatching([
      { rowKey: document.Id as Key, userPermissions: filteredUserPermissions },
    ]);

    try {
      toggleLoading(true);
      await updateDocumentUserPermission({ requests });
      message.success("Xóa quyền thành công");
      queryClient.invalidateQueries({ queryKey: ["document-content"] });
    } catch (error) {
      console.error(error);
      message.error("Xóa quyền thất bại");
    } finally {
      toggleLoading(false);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Nội dung",
      dataIndex: "Content",
      key: "Content",
      render: (value: string) => {
        const doc = html.htmlStringToObject(value);
        const tree = html.getTreeData(doc);
        const htmlString = html.objectToHtmlString(tree);
        return <span dangerouslySetInnerHTML={{ __html: htmlString }} />;
      },
    },
    {
      title: (
        <DecentralizeTitleTable
          disabled={disabledSelectUsers}
          label="Danh sách người xem"
          docEditors={docEditors}
          onSubmit={onDecentralizeRead}
        />
      ),
      dataIndex: "UserPermissions",
      key: "readUsers",
      width: "400px",
      render: (users, document) => (
        <DocumentGrantedUsers
          roles={[Role.read]}
          userPermissions={users}
          onRevoke={(data) => onRevoke(data, document)}
        />
      ),
    },
    {
      title: (
        <DecentralizeTitleTable
          disabled={disabledSelectUsers}
          label="Danh sách người chỉnh sửa"
          docEditors={docEditors}
          onSubmit={onDecentralizeEdit}
        />
      ),
      dataIndex: "UserPermissions",
      width: "400px",
      key: "editUsers",
      render: (users, document) => (
        <DocumentGrantedUsers
          roles={[Role.update]}
          userPermissions={users}
          onRevoke={(data) => onRevoke(data, document)}
        />
      ),
    },
  ];

  const rowSelection: TableRowSelection<IDocumentContent> = {
    checkStrictly: false,
    selectedRowKeys,
    onChange: (value) => setSelectedRowKeys(value),
  };

  const mappedDocuments = arrayNest<IDocumentContent>(documents, {
    idFieldName: "CustomId",
    parentIdFieldName: "CustomParentId",
  });

  const sendEmail = ({ email, contents }: { email: string; contents: string[] }, messageParams: string) => {
    let url = `${detailUrl}/${document?.listItemId}?fileId=${document?.fileId}&action=file&index=2`;
    const dateFrom = moment(document?.effectiveFrom).format(DateFormat.DD_MM_YYYY);
    const dateTo = moment(document?.effectiveTo).format(DateFormat.DD_MM_YYYY);
    const subject = "Anh/Chị có tài liệu cần soạn thảo";

    const sendMailParams: SendMailProps = {
      message: messageParams,
      docCode: document?.docCode ?? "",
      Title: document?.Title ?? "",
      contents: contents,
      dateFrom: dateFrom ?? "",
      dateTo: dateTo ?? "",
      url: url,
      userDisplayName: document?.userDisplayName ?? "",
      userEmail: document?.userEmail ?? "",
    };
    const content = mailComposeTemplate(sendMailParams);
    MailService.sendMailByBotit({ content, subject, toMail: email });
    // const message = MailService.buildMailMesssage(subject, content, "html", email);
    // MailService.sendMail(message);
  };

  const handleSendEmail = () => {
    let message: string = "";

    const filteredSelectedUser = selectedUserRef.current.filter((e) => !e.isContent);
    const emailContentMap: EmailContentMap = {};

    filteredSelectedUser.forEach((item) => {
      item.emails.forEach((email) => {
        if (!emailContentMap[email]) {
          emailContentMap[email] = [];
        }
        emailContentMap[email].push(item.content);
      });
    });

    const result = Object.keys(emailContentMap).map((email) => ({
      email,
      contents: emailContentMap[email],
    }));

    if (result.length === 0) {
      CreateNotification({
        title: "Thông báo",
        body: "Danh sách phân quyền trống!",
        icon: "/icons/warning.png",
      });
    }

    Modal.confirm({
      title: "Thông báo",
      content: (
        <Input.TextArea
          rows={4}
          placeholder="Vui lòng nhập nội dung"
          onChange={(e: any) => (message = e.target.value)}
        />
      ),
      okText: "Xác nhận",
      okType: "danger",
      okButtonProps: { type: "primary" },
      cancelText: "Huỷ",
      onOk() {
        result.forEach((e) => sendEmail({ ...e }, message));
        selectedUserRef.current = [];
        CreateNotification({
          title: "Thông báo",
          body: "Gửi email thành công!",
          icon: "/icons/success.png",
        });
        // sendEmail(distinctListEmail, message);
      },
    });
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 10,
          flexDirection: "row-reverse",
          position: "sticky",
          top: 70,
          right: 20,
          zIndex: 10,
          marginBottom: "4px",
        }}
      >
        <Button className="" variant="danger" onClick={() => handleSendEmail()}>
          Gửi mail thông báo
        </Button>
      </div>
      <Table
        rowKey="Id"
        loading={loading}
        bordered
        className="ant-table-row-actions-disabled"
        columns={columns}
        rowSelection={rowSelection}
        dataSource={mappedDocuments}
        pagination={false}
        scroll={{ x: 1600, y: 500 }}
        expandable={{
          defaultExpandAllRows: true,
          expandIcon: ({ expanded, onExpand, record }) => {
            if (record.children?.length === 0) return null;
            return (
              <DownCircleOutlined
                rotate={expanded ? 0 : 180}
                style={{ fontSize: 20 }}
                rev="data"
                onClick={(e) => onExpand(record, e)}
              />
            );
          },
        }}
      />
    </>
  );
};

export default DocumentViewDecentralize;
